<script setup>
  /* global defineProps, defineEmits */
  import { computed, ref, watch } from 'vue'
  import { formatReplayLanguage } from './replayLabels'
  import { replayLanguageKey } from '../../composables/useReplayRangeAnalysis'
  const props = defineProps({
    plan: { type: Object, default: null },
    busy: Boolean,
    generationBusy: Boolean,
    error: { type: String, default: '' },
  })
  const emit = defineEmits(['confirm', 'back'])
  const accepted = ref(false)
  watch(
    () => props.plan?.preflightId,
    () => {
      accepted.value = false
    }
  )
  const duration = (ms) => {
    const seconds = Math.ceil((ms || 0) / 1000)
    return `${Math.floor(seconds / 3600)} 小时 ${Math.floor((seconds % 3600) / 60)} 分 ${seconds % 60} 秒`
  }
  const basic = computed(() => props.plan?.result === 'MATERIALS_ONLY')
  const dubbing = computed(() =>
    (props.plan?.languageOptions || []).filter((option) =>
      (props.plan?.languages || []).some(
        (language) => replayLanguageKey(language) === replayLanguageKey(option.language)
      )
    )
  )
  const back = () => {
    emit('back')
  }
</script>
<template>
  <el-dialog
    :close-on-click-modal="false"
    :model-value="true"
    title="确认本次回顾视频"
    width="min(760px, 94vw)"
    @close="back"
  >
    <div v-if="plan" class="preflight">
      <strong>
        {{ plan.generationIntent === 'FULL' ? '整场回顾' : '阶段回顾' }} · {{ duration(plan.durationMs) }}
      </strong>
      <p v-if="plan.skippedBreakMs > 0">已跳过休会 {{ duration(plan.skippedBreakMs) }}，不计入补静音时长。</p>
      <details v-if="plan.skippedRanges?.length">
        <summary>已跳过的时间段</summary>
        <p v-for="(range, index) in plan.skippedRanges" :key="index" class="period">
          {{ new Date(range.startAtMs).toLocaleString() }} — {{ new Date(range.endAtMs).toLocaleString() }}
        </p>
      </details>
      <p v-if="plan.estimatedAudioMs != null">
        原声版本：预计保留原声 {{ duration(plan.estimatedAudioMs) }}；约
        {{ duration(Math.max(0, plan.durationMs - plan.estimatedAudioMs)) }} 补静音。
      </p>
      <p v-else>原声版本：时长待核实，制作时解码已保存原声并保留可用内容。</p>
      <p v-if="plan.originalSubtitleCount != null">
        原文字幕 {{ plan.originalSubtitleCount }} 条 · PPT {{ plan.pptEvents }} 次
      </p>
      <div v-if="dubbing.length" class="dubbing-summary">
        <strong>另生成字幕配音</strong>
        <p v-for="option in dubbing" :key="option.language">
          {{ formatReplayLanguage(option.language) }} · {{ option.subtitleCount }} 条字幕 · 覆盖
          {{ duration(option.coverageMs) }}
        </p>
        <p class="period">配音按所选范围内的字幕生成，独立于原声覆盖时长。</p>
      </div>
      <div v-for="(range, index) in plan.ranges" :key="index" class="period">
        {{ new Date(range.startAtMs).toLocaleString() }} — {{ new Date(range.endAtMs).toLocaleString() }}
      </div>
      <img v-if="plan.bannerUrl" alt="缺失画面使用的会议 banner" :src="plan.bannerUrl" />
      <p v-else>缺失画面使用会议名称和日期封面。</p>
      <el-alert
        v-if="basic"
        :closable="false"
        title="本次主要为会议资料画面与静音，不包含完整现场内容"
        type="warning"
      />
      <ul>
        <li v-for="issue in plan.issues" :key="issue.code">
          <strong>{{ issue.message }}</strong>
          <p>{{ issue.action }}</p>
        </li>
      </ul>
      <el-checkbox v-model="accepted" :disabled="busy">
        {{
          plan.generationIntent === 'FULL' ? '确认会议已结束，' : ''
        }}确认所选范围；制作中发现缺失时，保留可用内容，以会议背景和静音补齐
      </el-checkbox>
      <p v-if="generationBusy">当前会议正在生成，请完成后再提交。</p>
      <p v-if="error" class="error" role="alert">{{ error }}</p>
    </div>
    <template #footer>
      <el-button @click="back">返回调整</el-button>
      <el-button :disabled="!accepted || generationBusy" :loading="busy" type="primary" @click="emit('confirm')">
        {{ basic ? '确认生成基础版' : '确认并生成' }}
      </el-button>
    </template>
  </el-dialog>
</template>
<style scoped>
  .preflight {
    line-height: 1.7;
  }
  .preflight img {
    max-width: 240px;
    max-height: 135px;
    margin: 12px 0;
    object-fit: contain;
  }
  .period {
    color: #67758a;
    font-size: 13px;
  }
  li {
    padding: 8px 0;
  }
  li p {
    margin: 0;
    color: #67758a;
  }
  .preflight :deep(.el-checkbox) {
    height: auto;
    white-space: normal;
    align-items: start;
  }
  .preflight :deep(.el-checkbox__label) {
    white-space: normal;
    line-height: 1.6;
  }
  .error {
    color: #bc3333;
  }
</style>
